import { Injectable } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { ResourceVm } from 'qp-ui-interface';
import { LocalStorageService } from 'src/app/shared/services/storage/local-storage.service';
import { KEYS } from 'src/app/shared/utils/constants/storage.constants';
import { PageType } from '../enums/PageType';
import { CommonEventAttributes } from '../interface/appEvent.interface';
import { CommonAttributeFunc } from '../utils/commonAttributeFunc';
import { NewrelicStore } from './newrelic.store';

@Injectable({
  providedIn: 'root',
})
export class NewrelicAppStore {
  constructor(
    private readonly localStorage: LocalStorageService,
    private readonly commonAttributeFunc: CommonAttributeFunc,
    private readonly translate: TranslateService,
    private readonly newrelicStore: NewrelicStore
  ) {}

  private commonAttributes: CommonEventAttributes | null = null;

  public getCommonAttributes(): CommonEventAttributes {
    this.commonAttributes = this.createCommonAttributes();
    return this.commonAttributes;
  }

  public setCommonAttributes(attributes: CommonEventAttributes): void {
    this.commonAttributes = attributes;
  }

  private createCommonAttributes(): CommonEventAttributes {
    const { contentLanguage, preferredLanguage } =
      this.commonAttributeFunc.getLanguageInfo();
    const { silentLogin, baseSubscription } =
      this.commonAttributeFunc.getSubscriptionInfo();
    const { userID, profileID } = this.commonAttributeFunc.getUserInfo();
    const deviceID = this.commonAttributeFunc.getDeviceID();
    const userCategory = this.commonAttributeFunc.getUserCategory();
    const userType = this.commonAttributeFunc.getUserType();

    return {
      // TODO: get app version from package.json
      appVersion: window.appVersion || undefined,
      appBuild: window.buildVersion || undefined,
      deviceID,
      userID,
      userType,
      userCategory,
      profileID,
      silentLogin,
      baseSubscription,
      // displayLanguage,
      contentLanguage,
      preferredLanguage,
      ...this.newrelicStore.getDeviceInfo(),
      ...this.newrelicStore.getAsnInfo(),
      ...this.newrelicStore.getUserRegionInfo(),
    };
  }

  private getContentInfo(currentResource: ResourceVm) {
    return {
      storefrontID:
        localStorage.getItem('storefrontId') ||
        localStorage.getItem('CurrentStorefrontId'),
      providerID: currentResource?.providerName || undefined,
      contentKey:
        currentResource?.key !== 'view-all' ? currentResource?.key : undefined,
      contentID:
        currentResource?.id !== 'view-all' ? currentResource?.id : undefined,
      containerID: currentResource?.containerId || undefined,
      contentType:
        currentResource?.contentType !== 'view-all'
          ? currentResource?.contentType
          : undefined,
      contentGenre: currentResource?.contentGenre?.[this.translate.currentLang]
        ? currentResource?.contentGenre?.[this.translate.currentLang].join(
            ' , '
          )
        : undefined,
      contentName: currentResource?.title?.[this.translate.currentLang]
        ? currentResource?.title?.[this.translate.currentLang]
        : undefined,
      isFreePreview: currentResource?.freePreview !== undefined,
      containerName:
        currentResource?.containerName?.[this.translate.currentLang] || undefined,
      tabID: this.localStorage.getItem(KEYS.CURRENT_TAB_ID) || undefined,
      tabName: this.localStorage.getItem(KEYS.CURRENT_TAB_NAME) || undefined,
      seriesName:
        currentResource?.contentType === 'webseries' ||
        currentResource?.contentType === 'webepisode'
          ? currentResource?.seriesTitle?.[this.translate.currentLang] || undefined
          : undefined,
      collectionID: currentResource?.collectionID || undefined,
      collectionName:
        currentResource?.collectionName?.[this.translate.currentLang] || undefined,
      searchItemPosition: currentResource?.ex_id || undefined,
    };
  }

  private getCollectionInfoLocal(currentResource: ResourceVm) {
    return {
      collectionID: currentResource?.collectionID || undefined,
      collectionName:
        currentResource?.collectionName?.[this.translate.currentLang] || undefined,
    };
  }

  public getRentContentInfo(currentResource: ResourceVm, priceCode: string) {
    return {
      ...this.getContentInfo(currentResource),
      pageID: PageType.Details,
      priceCode,
      // containerID: string; // for search null or from carousel send the containerVm ID
    };
  }

  public getFavoriteContentInfo(
    currentResource: ResourceVm,
    ccEnabled: boolean,
    isFavorite: boolean,
    seasonNumber: string,
    episodeNumber: string
  ) {
    return {
      ...this.getContentInfo(currentResource),
      ...this.getCollectionInfoLocal(currentResource),
      pageID: PageType.Details,
      favoriteAction: isFavorite ? 'ADD' : 'DELETE',
      ccEnabled:
        ccEnabled === undefined || ccEnabled === null ? undefined : ccEnabled,
      seasonNumber,
      episodeNumber,
      // seriesName: currentResource?.isSeries ? currentResource?.seriesName : 'NA', // While click on series need to send the series name other wise send NA
    };
  }

  public getCollectionInfo(currentResource: ResourceVm, pageID?: PageType) {
    return {
      pageID: pageID || PageType.Details,
      ...this.getContentInfo(currentResource),
      ...this.getCollectionInfoLocal(currentResource),
    };
  }

  public getInAppBannerClickInfo() {
    return {
      pageID: PageType.Home,
      tabID: this.localStorage.getItem(KEYS.CURRENT_TAB_ID) || undefined,
      tabName: this.localStorage.getItem(KEYS.CURRENT_TAB_NAME) || undefined,
    };
  }

  public getContentDetails(
    currentResource: ResourceVm,
    pageID?: PageType,
    searchItemPosition?: number
  ) {
    return {
      pageID: pageID || PageType.Details,
      ...this.getContentInfo(currentResource),
      searchItemPosition: searchItemPosition || undefined,
    };
  }
}
